import type { CSSProperties, HTMLAttributes } from "react";
import { useT } from "../../lib/i18n.js";
import "./StorageMeter.css";

type SegmentKey = "originals" | "derivatives" | "video" | "exports";

/** The same four figures, grouped — the shape the API returns. */
export type StorageByClass = Record<SegmentKey, number>;

export interface StorageMeterProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** All figures in bytes. Supply these or `byClass`. */
  originals?: number;
  derivatives?: number;
  video?: number;
  exports?: number;
  /** Grouped alternative to the four flat props. Takes precedence. */
  byClass?: StorageByClass;
  limit?: number;
  /** Alias for `limit`, matching the API's field name. */
  limitBytes?: number;
  /** Heading above the bar. */
  label?: string;
  showLegend?: boolean;
}

const SEGMENT_LABELS: Record<SegmentKey, string> = {
  originals: "Originals",
  derivatives: "Derivatives",
  video: "Video",
  exports: "Exports",
};

const SEGMENT_ORDER: SegmentKey[] = [
  "originals",
  "derivatives",
  "video",
  "exports",
];

/** The point at which the meter stops being informational and starts warning. */
const CRITICAL_RATIO = 0.95;

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const exponent = Math.min(
    UNITS.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const value = bytes / 1024 ** exponent;
  const unit = UNITS[exponent] ?? "B";
  const decimals = value < 10 && exponent > 0 ? 1 : 0;

  return `${value.toFixed(decimals)} ${unit}`;
}

/**
 * A segmented quota bar. Every category keeps its own colour in the legend
 * even past the critical threshold, where the used portion turns
 * `--fc-danger` — and the figure turns with it, so colour is never the only
 * signal (§10.5).
 */
export function StorageMeter({
  originals,
  derivatives,
  video,
  exports,
  byClass,
  limit,
  limitBytes,
  label,
  showLegend = true,
  className,
  ...rest
}: StorageMeterProps) {
  const t = useT();
  const sizes: StorageByClass = {
    originals: byClass?.originals ?? originals ?? 0,
    derivatives: byClass?.derivatives ?? derivatives ?? 0,
    video: byClass?.video ?? video ?? 0,
    exports: byClass?.exports ?? exports ?? 0,
  };

  const used = SEGMENT_ORDER.reduce(
    (total, key) => total + Math.max(0, sizes[key]),
    0,
  );
  const resolvedLimit = limit ?? limitBytes ?? 0;
  const safeLimit = resolvedLimit > 0 ? resolvedLimit : Math.max(used, 1);
  const ratio = used / safeLimit;
  const critical = ratio > CRITICAL_RATIO;

  const percentOf = (bytes: number) =>
    `${(Math.min(1, Math.max(0, bytes) / safeLimit) * 100).toFixed(4)}%`;

  const classes = ["fc-storage", critical ? "is-critical" : null, className]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  const summary = `${formatBytes(used)} of ${formatBytes(safeLimit)} used. ${SEGMENT_ORDER.map(
    (key) => `${t(SEGMENT_LABELS[key])} ${formatBytes(sizes[key])}`,
  ).join(", ")}.`;

  return (
    <div {...rest} className={classes}>
      <div className="fc-storage__header">
        <span className="fc-storage__label">{label ?? t("Storage")}</span>
        <span className="fc-storage__figure">
          {`${formatBytes(used)} of ${formatBytes(safeLimit)}`}
        </span>
      </div>

      <div className="fc-storage__bar" role="img" aria-label={summary}>
        {SEGMENT_ORDER.map((key) => {
          const bytes = Math.max(0, sizes[key]);
          if (bytes <= 0) return null;
          const style: CSSProperties = { width: percentOf(bytes) };
          return (
            <span
              key={key}
              className={`fc-storage__segment fc-storage__segment--${key}`}
              style={style}
            />
          );
        })}
      </div>

      {showLegend ? (
        <ul className="fc-storage__legend">
          {SEGMENT_ORDER.map((key) => (
            <li key={key} className="fc-storage__legend-item">
              <span
                className={`fc-storage__swatch fc-storage__swatch--${key}`}
                aria-hidden="true"
              />
              <span className="fc-storage__legend-label">
                {t(SEGMENT_LABELS[key])}
              </span>
              <span className="fc-storage__legend-value">
                {formatBytes(sizes[key])}
              </span>
            </li>
          ))}
          <li className="fc-storage__legend-item">
            <span
              className="fc-storage__swatch fc-storage__swatch--free"
              aria-hidden="true"
            />
            <span className="fc-storage__legend-label">{t("Free")}</span>
            <span className="fc-storage__legend-value">
              {formatBytes(Math.max(0, safeLimit - used))}
            </span>
          </li>
        </ul>
      ) : null}

      {critical ? <p className="fc-storage__warning">Almost full</p> : null}
    </div>
  );
}
